import {injectable} from 'inversify';
import {DeviceType} from '@fantasmagames/core/src/device/DeviceType';

declare type DeviceCallback = (device: Device) => void;

@injectable()
export class Device {
	/**
	 * The time the device became ready
	 */
	private _deviceReadyAt: number;

	/**
	 * Initialization has completed
	 */
	private _initialized: boolean;

	private _desktop: boolean;
	private _iOS: boolean;
	private _iosChrome: boolean;

	/**
	 * If running in iOS this will contain the major version number
	 */
	private _iOSVersion: number;

	private _android: boolean;
	private _chromeOS: boolean;
	private _linux: boolean;
	private _macOS: boolean;
	private _windows: boolean;
	private _windowsPhone: boolean;
	private _canvas: boolean;
	private _webGL: boolean;
	private _localStorage: boolean;
	private _touch: boolean;
	private _chrome: boolean;

	/**
	 * If running in Chrome this will contain the major version number
	 */
	private _chromeVersion: number;

	private _firefox: boolean;

	/**
	 * If running in Firefox this will contain the major version number
	 */
	private _firefoxVersion: number;
	/**
	 *  Set to true if running in Internet Explorer
	 */
	private _ie: boolean;
	/**
	 * If running in Internet Explorer this will contain the major version number.
	 * Beyond IE10 you should use this.trident and this.tridentVersion
	 */
	private _ieVersion: number;
	/**
	 * Set to true if running a Trident version of Internet Explorer (IE11+)
	 */
	private _trident: boolean;
	/**
	 * If running in Internet Explorer 11 this will contain the major version number.
	 * See {@link http://msdn.microsoft.com/en-us/library/ie/ms537503(v=vs.85).aspx}
	 */
	private _tridentVersion: number;
	/**
	 * Set to true if running in Microsoft Edge browser
	 */
	private _edge: boolean;

	/**
	 * Set to true if running in Mobile Safari
	 */
	private _mobileSafari: boolean;

	private _opera: boolean;
	private _samsungNativeBrowser: boolean = false;

	private _isOppoBrowser: boolean = false;

	private _iframe: boolean;
	private _safari: boolean;

	private _fullscreenDisabledByIntegration: boolean = false;

	/**
	 * If running in Safari this will contain the major version number
	 */
	private _safariVersion: number;

	/**
	 * Set to true if running as a WebApp, i.e. within a WebView
	 */
	private _webApp: boolean;

	/**
	 * Are Audio tags available?
	 */
	private _audioData: boolean;

	/**
	 * If supports WebAudio API this will contain AudioContext constructor?
	 */
	private _webAudioContextConstructor: typeof AudioContext;

	/**
	 * Can this device play ogg files?
	 */
	private _ogg: boolean;
	/**
	 * opus - Can this device play opus files?
	 */
	private _opus: boolean;
	/**
	 * Can this device play mp3 files?
	 */
	private _mp3: boolean;
	/**
	 * Can this device play wav files?
	 */
	private _wav: boolean;
	/**
	 * Can this device play m4a files?
	 */
	private _m4a: boolean;

	private _iPhone: boolean;

	private _iPhone4: boolean;

	private _iPhoneX: boolean;

	private _iPad: boolean;

	private _pixelRatio: number;
	private _webP: boolean;

	/**
	 * Does the browser support the Full Screen API?
	 */
	private _fullscreen: boolean;

	/**
	 * If the browser supports the Full Screen API this holds the call you need to use to activate it
	 */
	private _requestFullscreen: string;

	/**
	 * If the browser supports the Full Screen API this holds the call you need to use to cancel it
	 */
	private _cancelFullscreen: string;

	/**
	 * The Element that is currently being presented in full-screen mode in this document
	 */
	private _fullscreenElement: string;

	/**
	 * Does the browser support access to the Keyboard during Full Screen mode?
	 */
	private _fullscreenKeyboard: boolean;
	private _onInitialized: DeviceCallback;
	private _readyCheckListener: EventListenerOrEventListenerObject;
	private _iOSRunningAsDesktop: boolean = false;

	private _type: DeviceType;

	public constructor(onInitialized: DeviceCallback) {
		this._onInitialized = onInitialized;

		this._readyCheckListener = this._readyCheck.bind(this);
		if (document.readyState === 'complete' || document.readyState === 'interactive') {
			// why is there an additional timeout here?
			window.setTimeout(this._readyCheckListener, 0);
		} else {
			document.addEventListener('DOMContentLoaded', this._readyCheckListener, false);
			window.addEventListener('load', this._readyCheckListener, false);
		}
	}

	/**
	 * check whether the host environment can play audio
	 * @param type One of 'mp3, 'ogg', 'wav', 'opus'
	 * @returns {boolean}
	 */
	public canPlayAudio(type: string): boolean {
		if (type === 'mp3' && this._mp3) {
			return true;
		} else if (type === 'm4a' && this._m4a) {
			return true;
		} else if (type === 'ogg' && (this._ogg || this._opus)) {
			return true;
		} else if (type === 'opus' && this._opus) {
			return true;
		} else if (type === 'wav' && this._wav) {
			return true;
		}

		return false;
	}

	public getSoundFormat(): string {
		if (this.canPlayAudio('ogg')) {
			return 'ogg';
		} else if (this.canPlayAudio('m4a')) {
			return 'm4a';
		}

		return null;
	}

	public getSoundQuality(): string {
		if (PIXI.sound.useLegacy) {
			return 'low';
		} else if (this.desktop) {
			return 'hi';
		} else {
			return 'mid';
		}
	}

	private _readyCheck(): void {
		if (!document.body) {
			window.setTimeout(this._readyCheckListener, 20);
		} else if (!this._deviceReadyAt) {
			this._deviceReadyAt = Date.now();

			document.removeEventListener('DOMContentLoaded', this._readyCheckListener);
			window.removeEventListener('load', this._readyCheckListener);

			this._initialize();
			this._initialized = true;

			this._onInitialized(this);
		}
	}

	private _initialize(): void {
		// Run the checks
		this._checkOS();
		this._checkBrowser();
		this._checkAudio();
		this._checkDevice();
		this._checkFeatures();
		this._checkFullScreenSupport();
		this._checkIframe();
		this._checkWebP();
		this._checkType();
	}

	private _checkOS(): void {
		const ua = navigator.userAgent;

		if (/Playstation Vita/.test(ua)) {
			// this.vita = true;
		} else if (/Kindle/.test(ua) || /\bKF[A-Z][A-Z]+/.test(ua) || /Silk.*Mobile Safari/.test(ua)) {
			// this.kindle = true;
			// This will NOT detect early generations of Kindle Fire, I think there is no reliable way...
			// E.g. "Mozilla/5.0 (Macintosh; U; Intel Mac OS X 10_6_3; en-us; Silk/1.1.0-80)
			// AppleWebKit/533.16 (KHTML, like Gecko) Version/5.0 Safari/533.16 Silk-Accelerated=true"
		} else if (/Android/.test(ua)) {
			this._android = true;
		} else if (/CrOS/.test(ua)) {
			this._chromeOS = true;
		} else if (/iP[ao]d|iPhone/i.test(ua)) {
			this._iOS = true;
			navigator.appVersion.match(/OS (\d+)/);
			this._iOSVersion = parseInt(RegExp.$1, 10);
		} else if (/Linux/.test(ua)) {
			this._linux = true;
		} else if (/Mac OS/.test(ua)) {
			this._macOS = true;
		} else if (/Windows/.test(ua)) {
			this._windows = true;
		}

		// HEADSUP! Sneaky fix for Ipads running Request Desktop mode.
		// Should maybe work for Iphones too.
		// https://github.com/google/model-viewer/pull/779/files
		if (!this._iOS) {
			this._iOS = (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(self as any).MSStream) ||
				(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
			if (this._iOS) {
				// this._macOS = false;
				// this._desktop = false;
				this._iOSRunningAsDesktop = true;
			}
		}

		if (/Windows Phone/i.test(ua) || /IEMobile/i.test(ua)) {
			this._android = false;
			this._iOS = false;
			this._macOS = false;
			this._windows = true;
			this._windowsPhone = true;
		}

		const silk = /Silk/.test(ua); // detected in browsers

		if (this._windows || this._macOS || (this._linux && !silk) || this._chromeOS) {
			this._desktop = true;
		}

		// Windows Phone / Table reset
		if (this._windowsPhone || (/Windows NT/i.test(ua) && /Touch/i.test(ua))) {
			this._desktop = false;
		}

		this._iosChrome = ua.indexOf('CriOS') !== -1;

		this._touch = this._detectTouchscreen();
	}

	private _detectTouchscreen(): boolean {
		// From https://patrickhlauke.github.io/touch/touchscreen-detection/
		let result = false;
		if ((window as any).PointerEvent && ('maxTouchPoints' in navigator)) {
			// if Pointer Events are supported, just check maxTouchPoints
			if (navigator.maxTouchPoints > 0) {
				result = true;
			}
		} else {
			// no Pointer Events...
			if (window.matchMedia && window.matchMedia('(any-pointer:coarse)').matches) {
				// check for any-pointer:coarse which mostly means touchscreen
				result = true;
			} else if ((window as any).TouchEvent || ('ontouchstart' in window)) {
				// last resort - check for exposed touch events API / event handler
				result = true;
			}
		}

		return result;
	}

	private _checkFullScreenSupport(): void {
		const fs = [
			'requestFullscreen',
			'requestFullScreen',
			'webkitRequestFullscreen',
			'webkitRequestFullScreen',
			'msRequestFullscreen',
			'msRequestFullScreen',
			'mozRequestFullScreen',
			'mozRequestFullscreen',
		];

		const element = document.createElement('div');

		for (const fsValue of fs) {
			if (element[fsValue]) {
				this._fullscreen = true;
				this._requestFullscreen = fsValue;
				break;
			}
		}

		const cfs = [
			'cancelFullScreen',
			'exitFullscreen',
			'webkitCancelFullScreen',
			'webkitExitFullscreen',
			'msCancelFullScreen',
			'msExitFullscreen',
			'mozCancelFullScreen',
			'mozExitFullscreen',
		];

		if (this._fullscreen) {
			for (const cfsValue of cfs) {
				if (element[cfsValue]) {
					this._cancelFullscreen = cfsValue;
					break;
				}
			}
		}

		const fse = [
			'fullscreenElement',
			'fullScreenElement',
			'webkitFullscreenElement',
			'webkitCurrentFullScreenElement',
			'mozFullScreenElement',
			'msFullscreenElement',
		];

		if (this._fullscreen) {
			for (const fseValue of fse) {
				if (fseValue in document) {
					this._fullscreenElement = fseValue;
					break;
				}
			}
		}

		// Keyboard Input?
		if ((window as any).Element && (window as any).Element.ALLOW_KEYBOARD_INPUT) {
			this._fullscreenKeyboard = true;
		}
	}

	private _checkIframe(): void {
		// If the iFrame is cross origin it would throw an exception
		try {
			this._iframe = window.self !== window.top;
		} catch (e) {
			this._iframe = true;
		}
	}

	private _checkBrowser(): void {
		const ua = navigator.userAgent;

		if (/Edge\/\d+/.test(ua)) {
			this._edge = true;
		} else if (/OppoBrowser\/(\d+)/.test(ua) && !this._windowsPhone) {
			this._isOppoBrowser = true;
		} else if (/SamsungBrowser\/(\d+)/.test(ua) && !this._windowsPhone) {
			this._samsungNativeBrowser = true;
		} else if (/Chrome\/(\d+)/.test(ua) && !this._windowsPhone) {
			this._chrome = true;
			this._chromeVersion = parseInt(RegExp.$1, 10);
		} else if (/Firefox\D+(\d+)/.test(ua)) {
			this._firefox = true;
			this._firefoxVersion = parseInt(RegExp.$1, 10);
		} else if (/AppleWebKit/.test(ua) && this._iOS) {
			this._mobileSafari = true;
		} else if (/MSIE (\d+\.\d+);/.test(ua)) {
			this._ie = true;
			this._ieVersion = parseInt(RegExp.$1, 10);
		} else if (/Opera/.test(ua)) {
			this._opera = true;
		} else if (/Safari\/(\d+)/.test(ua) && !this._windowsPhone) {
			this._safari = true;

			if (/Version\/(\d+)\./.test(ua)) {
				this._safariVersion = parseInt(RegExp.$1, 10);
			}
		} else if (/Trident\/(\d+\.\d+)(.*)rv:(\d+\.\d+)/.test(ua)) {
			this._ie = true;
			this._trident = true;
			this._tridentVersion = parseInt(RegExp.$1, 10);
			this._ieVersion = parseInt(RegExp.$3, 10);
		}

		// WebApp mode in iOS
		if ((navigator as any).standalone) {
			this._webApp = true;
		}
	}

	private _checkAudio(): void {
		this._audioData = !!(window as any).Audio;
		this._webAudioContextConstructor = (window as any).AudioContext || (window as any).webkitAudioContext;

		const audioElement = document.createElement('audio');

		try {
			if (!!audioElement.canPlayType) {
				if (audioElement.canPlayType('audio/ogg; codecs="vorbis"').replace(/^no$/, '')) {
					this._ogg = true;
				}

				if (
					audioElement.canPlayType('audio/ogg; codecs="opus"').replace(/^no$/, '') ||
					audioElement.canPlayType('audio/opus;').replace(/^no$/, '')
				) {
					this._opus = true;
				}

				if (audioElement.canPlayType('audio/mpeg;').replace(/^no$/, '')) {
					this._mp3 = true;
				}

				// Mimetypes accepted:
				//   developer.mozilla.org/En/Media_formats_supported_by_the_audio_and_video_elements
				//   bit.ly/iphoneoscodecs
				if (audioElement.canPlayType('audio/wav; codecs="1"').replace(/^no$/, '')) {
					this._wav = true;
				}

				if (audioElement.canPlayType('audio/x-m4a;') || audioElement.canPlayType('audio/aac;').replace(/^no$/, '')) {
					this._m4a = true;
				}
			}
		} catch (e) {
			console.warn('Device check audio exception');
		}
	}

	private _checkDevice(): void {
		this._pixelRatio = window.devicePixelRatio || 1;
		this._iPhone = navigator.userAgent.toLowerCase().indexOf('iphone') !== -1;
		this._iPhone4 = this._pixelRatio === 2 && this._iPhone;
		this._iPad = navigator.userAgent.toLowerCase().indexOf('ipad') !== -1;

		const ratio = window.devicePixelRatio || 1;

		// Define the users device screen dimensions
		const screen = {
			width: window.screen.width * ratio,
			height: window.screen.height * ratio,
		};

		this._iPhoneX = this._iPhone && screen.width === 1125 && screen.height === 2436;

	}

	private _checkFeatures(): void {
		this._canvas = !!(window as any).CanvasRenderingContext2D;
		const webGL = (function (): boolean {
			try {
				const canvas: HTMLCanvasElement = document.createElement('canvas');

				return !!((window as any).WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
			} catch (e) {
				return false;
			}
		})();
		this._webGL = !!webGL;
	}

	private _checkWebP(): void {
		const elem = document.createElement('canvas');

		if (!!(elem.getContext && elem.getContext('2d'))) {
			// was able or not to get WebP representation
			this._webP = elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
		} else {
			// very old browser like IE 8, canvas not supported
			this._webP = false;
		}
	}

	private _checkType(): void {
		if (this._desktop) {
			this._type = DeviceType.DESKTOP;
		} else {
			this._type = DeviceType.MOBILE;
		}
	}

	/**
	 * is 4:3
	 * @returns {boolean}
	 */
	private isIpadResolution(): boolean {
		const ratio = window.devicePixelRatio || 1;
		const screenWidth = Math.max(screen.width, screen.height) * ratio;
		const width = screenWidth;
		const height = Math.min(screen.width, screen.height) * ratio;

		return Math.abs(height / width - 0.75) < 0.04;
	}

	public get canvas(): boolean {
		return this._canvas;
	}

	public get webGL(): boolean {
		return this._webGL;
	}

	public get webp(): boolean {
		return this._webP;
	}

	public set webGL(value: boolean) {
		this._webGL = value;
	}

	public get chrome(): boolean {
		return this._chrome;
	}

	public get chromeVersion(): number {
		return this._chromeVersion;
	}

	public get iOSVersion(): number {
		return this._iOSVersion;
	}

	public get firefox(): boolean {
		return this._firefox;
	}

	public get audioData(): boolean {
		return this._audioData;
	}

	public get webAudioContextConstructor(): typeof AudioContext {
		return this._webAudioContextConstructor;
	}

	public get cancelFullscreen(): string {
		return this._cancelFullscreen;
	}

	public get fullscreenElement(): string {
		return this._fullscreenElement;
	}

	public get mobileSafari(): boolean {
		return this._mobileSafari;
	}

	public get webApp(): boolean {
		return this._webApp;
	}

	public get android(): boolean {
		return this._android;
	}

	public get iPhone4(): boolean {
		return this._iPhone4;
	}

	public get iPhoneX(): boolean {
		return this._iPhoneX;
	}

	public get iPhone(): boolean {
		return this._iPhone;
	}

	public get iOS(): boolean {
		return this._iOS;
	}

	public get isSafari(): boolean {
		return this._safari;
	}

	public get safariVersion(): number {
		return this._safariVersion;
	}

	public get fullscreen(): boolean {
		return this._fullscreen;
	}

	public get requestFullscreen(): string {
		return this._requestFullscreen;
	}

	public get desktop(): boolean {
		return this._desktop;
	}

	public get isMobile(): boolean {
		return !this._desktop;
	}

	public get isIOSChrome(): boolean {
		return this._iosChrome;
	}

	public get isOppoBrowser(): boolean {
		return this._isOppoBrowser;
	}

	public get isIE(): boolean {
		return this._ie;
	}

	public get IEVersion(): number {
		return this.isIE ? this._ieVersion : 0;
	}

	public get isMacOs(): boolean {
		return this._macOS;
	}

	public get isSamsungNative(): boolean {
		return this._samsungNativeBrowser;
	}

	public get isIframe(): boolean {
		return this._iframe;
	}

	public set fullscreenDisabledByIntegration(isDisabled: boolean) {
		this._fullscreenDisabledByIntegration = isDisabled;
	}

	public get isFullscreenDisabledByIntegration(): boolean {
		return this._fullscreenDisabledByIntegration;
	}

	public get iPad(): boolean {
		return this._iPad;
	}

	public get type(): DeviceType {
		return this._type;
	}

	public get hasTouch(): boolean {
		return this._touch;
	}

	public get isIOSMobileRequestingDesktop(): boolean {
		return this._iOSRunningAsDesktop;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/device/Device.ts